/**
 * useSyncStatus.ts
 *
 * Live-querying hook for sync health state.
 * Re-renders automatically when syncMeta changes in IndexedDB.
 *
 * New hook — no equivalent in old context layer.
 * Replaces: isContentSynced, isDirtyRef, isTitleDirtyRef from NoteContext
 *
 * Usage:
 *   const { syncStatus, pendingCount, isOffline, hasPending, lastSyncAt } = useSyncStatus()
 */

"use client";

import { useLiveQuery } from "dexie-react-hooks";
import { appDB } from "../db/client";
import type { SyncStatus } from "../db/schema";

interface UseSyncStatusReturn {
  // Current sync engine state
  syncStatus: SyncStatus;

  // How many operations are waiting to sync
  pendingCount: number;

  // True if browser is offline
  isOffline: boolean;

  // True if there are unsaved local changes
  hasPending: boolean;

  // Timestamp of last successful sync (null if never synced)
  lastSyncAt: number | null;

  // Stable device ID for this browser
  clientId: string;

  // Human-readable status message for UI
  statusLabel: string;
}

/**
 * Returns the current sync status from IndexedDB, live-updating.
 * Use this to render "Saving...", "All changes saved", "Offline" indicators.
 */
export function useSyncStatus(): UseSyncStatusReturn {
  const meta = useLiveQuery(
    () => appDB.syncMeta.get("global"),
    []
  );

  const syncStatus: SyncStatus = meta?.syncStatus ?? "idle";
  const pendingCount = meta?.pendingCount ?? 0;
  const isOffline = syncStatus === "offline";
  const hasPending = pendingCount > 0;
  const lastSyncAt = meta?.lastSuccessfulSyncAt ?? null;
  const clientId = meta?.clientId ?? "";

  // Human-readable label for UI indicator
  let statusLabel = "All changes saved";
  if (isOffline && hasPending) {
    statusLabel = "Offline — changes saved locally";
  } else if (isOffline) {
    statusLabel = "Offline";
  } else if (syncStatus === "syncing") {
    statusLabel = "Saving…";
  } else if (syncStatus === "error" && hasPending) {
    statusLabel = "Some changes failed to save";
  } else if (hasPending) {
    statusLabel = "Saving…";
  }

  return {
    syncStatus,
    pendingCount,
    isOffline,
    hasPending,
    lastSyncAt,
    clientId,
    statusLabel,
  };
}
